import { api } from '../api/client';
import { normalizeId } from '../utils/ids';
import {
	API_NOTIFICATIONS_CATALOG_URL,
	API_NOTIFICATIONS_URL,
	API_NOTIFICATION_DEFS_URL,
	API_NOTIFICATION_DEF_DISABLE_URL,
	API_NOTIFICATION_DEF_PREVIEW_URL,
	API_NOTIFICATION_DEF_TEST_URL,
	API_NOTIFICATION_DEF_URL,
	API_NOTIFICATION_SETTINGS_URL,
} from '../utils';

/**
 * Two surfaces, one subsystem: the INBOX (what already reached you) below,
 * and the CATALOG (what can reach you, and how) at the foot of the file.
 *
 * ── The catalog wire contract ────────────────────────────────────────────
 * ⚠ THE API IS THE AUTHORITY, not this comment and not the plan. What is
 * written here is read off the shipped handlers — keepr-api
 * `routes/notificationDefRoutes.js`, `utils/notifications/defs.js` and
 * `utils/notifications/settings.js` — and where the two ever disagree, the
 * API is right and this file is the thing that has to move.
 *
 *   GET /api/notifications/catalog?scope=<tier>&collection_id=<id>
 *       ⚠ `scope` DEFAULTS TO 'user' WHEN ABSENT, so the collection tier has
 *       to say so: a bare `?collection_id=` answers that collection's
 *       PERSONAL catalog, which is a different list.
 *   -> { tier, channels[], groups[], rows[], truncated? }
 *
 *   channels[]  { key, label, available, reason?, order }
 *   groups[]    { key, label, collection_id, hue, count }
 *   rows[]      { key, automation_id, group, name, category, severity,
 *                 trigger: { summary, … },
 *                 audience: { summary, count, userIds? },
 *                 escalation: { steps },
 *                 definedAt: 'global' | 'collection' | 'user',
 *                 channels: { email|sms|push: { value, source, locked } },
 *                 lockReason, muted, mutedUntil, mutedSource,
 *                 canEdit, canDelete }
 *   truncated   { collections } on the account tier only — the catalog walks
 *               at most 25 collections and says what it dropped.
 *
 *   `source` ∈ 'user' | 'collection' | 'global' | 'default' — WHO decided,
 *   not only what was decided, because the list has to say so out loud.
 *   `mutedSource` is the same vocabulary for the MUTE, which `source` never
 *   answers for: without it a row muted by a tier above the reader cannot be
 *   told from one they muted themselves, and "Reset to inherited" is offered
 *   where there is nothing to remove.
 *
 *   PUT /api/notification-settings
 *       { scope, collection_id?, key, muted?, mutedUntil?, channels?, locked?,
 *         lockReason? }                        ⚠ and nothing else: the Joi
 *       schema is `.unknown(false)`, so an extra key is a 400.
 *   -> { setting, resolved } — the stored tier row and the whole merge, NOT a
 *      catalog rows[] entry. 201 the first time a tier row is written, 200
 *      after that.
 *
 *   DELETE /api/notification-settings/{key}?scope=&collection_id=
 *   -> { ok: true, removed: 0 | 1 } — the tier row is dropped, and a tier that
 *      held nothing is already reset.
 *
 * **The tri-state vocabulary is `patchPreferences`' verbatim** (keepr-api
 * utils/preferenceUtils.js): `true` and `false` are explicit, **`null`
 * resets that channel to inherited**, and **an absent key means don't
 * touch it**. Four wire values over a three-state store, already shipped
 * and tested. There is no `"inherit"` string, and inventing one here would
 * give the platform two vocabularies for one idea.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * The in-app notification inbox — the rows the automations `notify` action
 * writes. Strictly per-user on the API side (the token is the whole scope), so
 * nothing here takes a collection or checks a permission.
 *
 * There is deliberately no single-notification GET on the API: the list IS the
 * read surface. Callers that need one row by id (the item form's `?notification=`
 * prefill) resolve it from the already-loaded list, or fetch the list and find
 * it — never a per-row request that does not exist.
 */

/**
 * The counts travel in RESPONSE HEADERS rather than the body, so a filtered or
 * paged list still agrees with the bell on the true totals: `X-Unread-Count`
 * for the dot, `X-Open-Count` for "Showing N open". Axios lowercases header
 * keys, but the lookup is case-insensitive anyway — an interceptor, a mock, or
 * a future adapter is free to hand back the header as the API spells it. When
 * a header is missing entirely (a test double, a proxy that strips unknown
 * headers) counting the rows we DID get is the honest fallback: it can
 * undercount a paged list, never invent rows.
 */
const readHeader = ( headers, name ) => {
	if ( ! headers ) return undefined;
	if ( typeof headers.get === 'function' ) {
		const direct = headers.get( name );
		if ( direct !== undefined && direct !== null ) return direct;
	}
	const lower = name.toLowerCase();
	const key = Object.keys( headers ).find( k => k.toLowerCase() === lower );
	return key === undefined ? undefined : headers[ key ];
};

const countUnread = rows => rows.filter( row => row?.readAt === null || row?.readAt === undefined ).length;

// A row written before the alert feature has no `state` at all, so "not
// resolved" — never "state === 'open'" — is what Open means, on the server
// (`{state: {$ne: 'resolved'}}`) and here. utils/notificationCopy says the
// same thing for the rows the drawer renders; this is the count's copy of it.
const countOpen = rows => rows.filter( row => ( row?.alertStatus || row?.state ) !== 'resolved' ).length;

const parseCount = ( headers, name, fallback ) => {
	const parsed = Number.parseInt( readHeader( headers, name ), 10 );
	return Number.isFinite( parsed ) && parsed >= 0 ? parsed : fallback;
};

/**
 * Newest-first inbox page.
 *
 *   GET /api/notifications?state=open|all&limit=&cursor=
 *   -> rows + X-Unread-Count + X-Open-Count
 *
 * `state` is the drawer's Open · All switch; `cursor` is the paging token —
 * opaque, and the caller only ever hands back what this returned. `unread`
 * and `skip` are the older params, kept because the bell's own poll and any
 * caller written against the shipped API still pass them.
 *
 * The next cursor comes from `X-Next-Cursor` when the server sends one and
 * from the LAST ROW'S ID otherwise. That fallback is not a guess about the
 * server's cursor format — it is the id of the row we have already read, which
 * is the one thing both ends agree on — and it keeps "Load older" working
 * against a build that has not grown the header yet. `hasMore` is a full page:
 * a short page is the end of the list, whatever the header says.
 */
export async function getNotifications( { state, unread, limit, skip, cursor } = {} ) {
	const params = {};
	if ( state ) params.state = state;
	if ( unread ) params.unread = 1;
	if ( limit !== undefined ) params.limit = limit;
	if ( skip !== undefined ) params.skip = skip;
	if ( cursor ) params.cursor = cursor;

	const response = await api.get( API_NOTIFICATIONS_URL, { params } );
	const rows = Array.isArray( response?.data ) ? response.data : [];
	const last = rows.length ? rows[ rows.length - 1 ] : null;

	return {
		rows,
		unreadCount: parseCount( response?.headers, 'x-unread-count', countUnread( rows ) ),
		openCount: parseCount( response?.headers, 'x-open-count', countOpen( rows ) ),
		nextCursor: readHeader( response?.headers, 'x-next-cursor' ) || normalizeId( last?._id ) || null,
		hasMore: limit !== undefined && rows.length >= limit,
	};
}

/** Mark one row read. Owner-only server-side; a foreign id answers 404. */
export function markNotificationRead( notificationId ) {
	return api.post( `${ API_NOTIFICATIONS_URL }/${ notificationId }/read` );
}

/**
 * Mark one row UNREAD again — the row menu's "I am not done with this".
 *
 * The unread half of the read endpoint, spelled as its DELETE: reading a row
 * is a fact you can take back, and a second verb ("/unread") would have been a
 * second way to say one thing. ⚠ The API is growing this in parallel with the
 * drawer; until it lands the write answers 404 and the caller rolls the dot
 * back, which is the same thing it does for any failed write.
 */
export function markNotificationUnread( notificationId ) {
	return api.delete( `${ API_NOTIFICATIONS_URL }/${ notificationId }/read` );
}

/** Mark every unread row read in one request. */
export function markAllNotificationsRead() {
	return api.post( `${ API_NOTIFICATIONS_URL }/read-all` );
}

/**
 * Take one row out of this person's inbox.
 *
 * It removes the ROW, never the alert: the obligation behind it is shared, and
 * one recipient tidying their inbox must not close a med-administration
 * escalation for the shift. ⚠ Also being built in parallel.
 */
export function dismissNotification( notificationId ) {
	return api.delete( `${ API_NOTIFICATIONS_URL }/${ notificationId }` );
}

/**
 * Answer one open alert: the signed-in half of the response pair (the emailed
 * half is the public `/n/r/{code}` confirm page, which consumes a one-shot
 * token instead of a session).
 *
 *   POST /api/notifications/{id}/respond   { responseKey }  -> the updated row
 *
 * The answer comes back as the WHOLE row, already carrying `respondedBy` and
 * whatever the response did to `alertStatus` and `escalation` — so the drawer
 * replaces what it holds rather than guessing which of those a "Snooze 15m"
 * changed. `text` rides along for a free-text `answer`; the server ignores it
 * for every other kind.
 */
export async function respondToNotification( notificationId, responseKey, { text } = {} ) {
	const body = { responseKey };
	if ( text !== undefined && text !== null && text !== '' ) body.text = text;
	const { data } = await api.post( `${ API_NOTIFICATIONS_URL }/${ notificationId }/respond`, body );
	return data || null;
}

/* ── The catalog: what can reach you, at one of three tiers ──────────────── */

/**
 * Params for one tier. The tier IS the scope, and it is ALWAYS spelled:
 *
 *   'user'        scope=user                       the account list
 *   'collection'  scope=collection&collection_id=  the collection's tab
 *   'global'      scope=global                     the platform catalog
 *
 * ⚠ `scope` IS NOT OPTIONAL, whatever an earlier draft of this file assumed.
 * Both readers default a missing one rather than inferring it from the id —
 * the catalog to `'user'`, the definitions list to "every tier at once" — so
 * a bare `?collection_id=` asks for one list and gets another. A collection
 * tier with no id is refused here rather than answered with the wrong list.
 */
const scopeParams = ( tier, collectionId ) => {
	if ( tier === 'global' ) return { scope: 'global' };
	if ( tier === 'collection' ) {
		if ( ! collectionId ) throw new Error( 'A collection-tier notification catalog needs a collection id.' );
		return { scope: 'collection', collection_id: String( collectionId ) };
	}
	// The account tier has no collection; one collection's personal list does.
	return collectionId ? { scope: 'user', collection_id: String( collectionId ) } : { scope: 'user' };
};

/**
 * ⚠ THE ACCOUNT RUNG IS RESERVED FOR THE MASTERS.
 *
 * `{scope:'user'}` with no collection is the global-per-user row — "does keepr
 * email me AT ALL" — and the only key that legitimately lives there is
 * `MASTER_NOTIFICATION_KEY`. Every other key is resolved by the API at the rung
 * it is addressed to, so an ordinary notification written here is either a 404
 * (nothing defines that key account-wide) or, for a global-tier one, a change
 * to that key in EVERY collection at once. Both came out of one client bug —
 * the account page writing the list's scope instead of the row's — and this is
 * the invariant that makes the second, silent half impossible to reach again.
 *
 * A programmer error, not a user one, so it throws rather than rendering: the
 * shape is decided in code, and there is no input a person could give to
 * produce it.
 */
const assertRung = ( tier, collectionId, key ) => {
	if ( tier === 'user' && ! collectionId && key !== MASTER_NOTIFICATION_KEY ) {
		throw new Error(
			`Only "${ MASTER_NOTIFICATION_KEY }" may be written at the account rung; "${ key }" needs the collection it belongs to.`
		);
	}
};

/**
 * The list the three hosts render: rows with their settings already resolved
 * and their provenance attached. Answers `{tier, channels, groups, rows}`;
 * missing arrays are normalized to empty ones so no caller guards a shape.
 */
export async function getNotificationCatalog( { tier = 'user', collectionId } = {} ) {
	const { data } = await api.get( API_NOTIFICATIONS_CATALOG_URL, { params: scopeParams( tier, collectionId ) } );
	return {
		tier: data?.tier || tier,
		channels: Array.isArray( data?.channels ) ? data.channels : [],
		groups: Array.isArray( data?.groups ) ? data.groups : [],
		rows: Array.isArray( data?.rows ) ? data.rows : [],
		// Only the account tier can truncate, and it says so rather than
		// handing back a short list that looks complete. `null` when it did not.
		truncated: data?.truncated || null,
	};
}

/**
 * Write one row's settings at one tier.
 *
 * ⚠ THE ANSWER IS `{setting, resolved}`, NOT A CATALOG ROW. `setting` is the
 * stored tier row as it now stands and `resolved` is the whole merge —
 * `{value, source, locked, lockReason, muted, mutedUntil, mutedSource,
 * masterOff, available, unavailableReason}` — which is more than a rows[]
 * entry carries and shaped differently. A caller that replaces a catalog row
 * with this answer replaces it with the wrong thing. `applySavedSettings`
 * below is the one place the two shapes meet; every caller goes through it.
 *
 * `channels` is the tri-state map: `true`/`false` set, `null` resets that one
 * channel to inherited, an ABSENT key leaves it alone. Undefined values are
 * dropped on the way out for exactly that reason — sending `{ sms: undefined }`
 * would serialize away and read as "don't touch", but only by accident; this
 * drops it on purpose, and a caller that MEANS "reset" says `null`.
 *
 * Nothing else may travel: the payload schema is `.unknown(false)`, so one
 * stray key is a 400 for the whole write.
 */
export async function putNotificationSettings( { tier = 'user', collectionId, key, muted, mutedUntil, channels, locked, lockReason } ) {
	assertRung( tier, collectionId, key );
	const body = { scope: tier, key };
	const params = scopeParams( tier, collectionId );
	if ( params.collection_id ) body.collection_id = params.collection_id;
	if ( muted !== undefined ) body.muted = muted;
	if ( mutedUntil !== undefined ) body.mutedUntil = mutedUntil;
	if ( locked !== undefined ) body.locked = locked;
	if ( lockReason !== undefined ) body.lockReason = lockReason;
	if ( channels ) {
		const picked = Object.fromEntries(
			Object.entries( channels ).filter( ( [ , value ] ) => value !== undefined )
		);
		if ( Object.keys( picked ).length ) body.channels = picked;
	}

	const { data } = await api.put( API_NOTIFICATION_SETTINGS_URL, body );
	return data;
}

/**
 * The PUT's answer folded back onto the catalog row it came from.
 *
 * ⚠ THE TWO SHAPES ARE NOT THE SAME SHAPE, which is the whole reason this
 * exists. A catalog row carries `channels: {<key>: {value, source, locked}}`
 * plus `lockReason`, `muted`, `mutedUntil` and `mutedSource`; `resolved`
 * carries the merge as PARALLEL MAPS — `value: {<key>: bool}`,
 * `source: {<key>: tier}`, `locked` as a flat array of channel keys. A caller
 * that dropped the answer in whole would corrupt the row; a caller that
 * dropped it on the floor (which is what `if (saved && saved.key)` did —
 * `setting.key` is the catalog key, so the guard passed and the WRONG OBJECT
 * went in, or the guess was simply kept) leaves the reader looking at their
 * own optimistic guess until they reload.
 *
 * What the server decided and this therefore takes:
 *
 *   - `value` and `source` per channel. The account master is already folded
 *     into `value` (`settings.js applyMaster`), so a master veto — a switch the
 *     reader turned off somewhere else entirely — lands here rather than
 *     waiting for a reload.
 *   - `lockReason`, `muted`, `mutedUntil`, which are row-level either way.
 *   - `mutedSource`, which travels with them because it is the mute's own
 *     provenance and `source` never speaks for the mute. It decides whether
 *     the row's ⋯ offers an enabled "Reset to inherited"
 *     (`notificationCopy.hasOwnSettings`), so a write that moved the mute
 *     between tiers has to settle the flag here rather than leave a stale one
 *     standing until the next reload.
 *
 * What it deliberately does NOT take: the `locked` FLAG per cell. The row's
 * flag means *locked from where you stand* — computed from `lockedBy`, which
 * `publicResolved` keeps off the wire on purpose — so `resolved.locked` cannot
 * be mapped onto it without guessing whose lock it is, and guessing "Required"
 * onto a lock the reader themselves set would be a worse lie than a stale one.
 * The one sound inference is made: a channel that is no longer locked ANYWHERE
 * is not locked from here either, so that flag is cleared.
 *
 * A malformed or missing answer returns the row untouched — the caller's own
 * optimistic guess, which is what it had a moment ago.
 */
export function applySavedSettings( row, saved ) {
	const resolved = saved?.resolved;
	if ( ! row || ! resolved ) return row;

	const lockedNow = new Set( Array.isArray( resolved.locked ) ? resolved.locked : [] );
	const channels = Object.fromEntries(
		Object.keys( row.channels || {} ).map( key => {
			const before = row.channels[ key ] || {};
			return [ key, {
				...before,
				...( resolved.value && key in resolved.value ? { value: resolved.value[ key ] } : {} ),
				...( resolved.source && key in resolved.source ? { source: resolved.source[ key ] } : {} ),
				locked: Boolean( before.locked ) && lockedNow.has( key ),
			} ];
		} )
	);

	return {
		...row,
		channels,
		lockReason: resolved.lockReason ?? null,
		muted: Boolean( resolved.muted ),
		mutedUntil: resolved.mutedUntil ?? null,
		mutedSource: resolved.mutedSource ?? null,
	};
}

/**
 * Reset the whole row to inherited: a DELETE of this tier's settings row, so
 * the cascade falls through to whatever is beneath it. The `cardLayouts`
 * "Reset to inherited" idiom verbatim — the row is dropped, never blanked,
 * because a row of `null`s and no row at all must not mean two things.
 *
 * Idempotent: `{ok: true, removed: 0}` for a tier that held nothing, which is
 * the answer a double-click deserves — and the answer a caller has to READ,
 * because "reset" and "there was nothing here" are two different things to
 * tell somebody. So this hands back the body rather than the axios envelope,
 * like every other read in this file.
 */
export async function resetNotificationSettings( { tier = 'user', collectionId, key } ) {
	assertRung( tier, collectionId, key );
	const { data } = await api.delete( `${ API_NOTIFICATION_SETTINGS_URL }/${ encodeURIComponent( key ) }`, {
		params: scopeParams( tier, collectionId ),
	} );
	return data;
}

/**
 * The account masters' reserved key.
 *
 * The settings overlay is keyed by a notification's catalog `key`; the account
 * page's Email · Text · Push switches are the one row that stands for ALL of
 * them, so they need a key no authored notification can ever take. It matches
 * the catalog key grammar (`/^[a-z0-9][a-z0-9-]{1,48}$/`) on purpose — the API
 * reserves this one word rather than the store growing a second shape for a
 * row that is otherwise identical.
 */
export const MASTER_NOTIFICATION_KEY = 'all-notifications';

/**
 * Set one account master, at the user tier with no collection — the
 * global-per-user row.
 *
 * ⚠ This is where `userPreferences.notificationEmails` goes to retire. That
 * preference has carried the email master since the notify action shipped and
 * the API's email channel module reads `notificationSettings` FIRST, falling
 * back to `prefs.notificationEmails === false` while both stores are live. So
 * the switch writes here, the preference stays readable for one release, and
 * the API-side `retireNotificationEmails` maintenance task finishes the job:
 * it writes a `{scope:'user', key:'all-notifications', channels:{email:false}}`
 * row for everyone who had turned the preference off, then drops the
 * preference and the fallback with it.
 */
export function setMasterChannel( channelKey, value ) {
	return putNotificationSettings( { tier: 'user', key: MASTER_NOTIFICATION_KEY, channels: { [ channelKey ]: value } } );
}

/* ── The definitions: the rows the editor writes ─────────────────────────── */

/**
 * The raw tier rows, `cardLayouts`-style — not the catalog. The catalog is
 * what a READER sees (rows with settings resolved and provenance attached);
 * these are what an AUTHOR edits, one row per tier, and the cascade is
 * whole-row replacement rather than a field merge.
 *
 * ── The wire contract ────────────────────────────────────────────────────
 * ⚠ THE API IS THE AUTHORITY. Read off keepr-api
 * `routes/notificationDefRoutes.js` and `utils/notifications/defs.js`.
 *
 *   GET    /api/notification-defs?scope=&collection_id=
 *          -> { defs[], resolved: { <key>: automation_id } }   NOT a bare array
 *   POST   /api/notification-defs
 *   PATCH  /api/notification-defs/{id}          `key` and `scope` are IMMUTABLE
 *   DELETE /api/notification-defs/{id}          drops one tier row (soft)
 *   POST   /api/notification-defs/{id}/disable  { collection_id? }
 *   POST   /api/notification-defs/{id}/preview  { collection_id? }
 *   POST   /api/notification-defs/{id}/test     { collection_id? }
 *
 * ⚠ THERE IS NO `GET /api/notification-defs/{id}`. The list IS the read
 * surface for definitions, exactly as it is for the inbox — see
 * `getNotificationDef` below, which resolves one row out of it.
 *
 * A write body is `{scope, collection_id?, key, name, category, severity?,
 * availability?, enabled?, trigger, check?, audience?, channels?, template,
 * escalation?, resolution?, responses?}` — `category` and `template` are
 * REQUIRED, the words a person reads live under `template: {title, body}`, and
 * what closes an alert is `resolution`. A stored row carries more than any one
 * release renders — `description`, `severity`, `channels`, `forkedFrom`, a
 * `resolution.kind` from a later version. Nothing here projects any of it
 * away: the editor round-trips whatever it is handed
 * (components/notifications/notificationEditorModel.js), and this module's job
 * is to not get in the way of that.
 * ─────────────────────────────────────────────────────────────────────────
 */
export async function getNotificationDefs( { tier = 'collection', collectionId } = {} ) {
	const { data } = await api.get( API_NOTIFICATION_DEFS_URL, { params: scopeParams( tier, collectionId ) } );
	return {
		defs: Array.isArray( data?.defs ) ? data.defs : [],
		// Which row WINS each key here, so an editor can show the tier it is
		// editing and the tier it inherits from out of one request.
		resolved: data?.resolved || {},
		// A sub-collection's parent's collection-tier notifications, which
		// fire for its records too (keepr-api § Sub-collections) — read-only
		// here, each `inherited: true` with `inheritedFrom`.
		inherited: Array.isArray( data?.inherited ) ? data.inherited : [],
	};
}

/**
 * One definition by id — resolved out of the tier list, because the API has no
 * single-definition GET and never did. The list is scoped by the same two
 * params every other read takes, and a row this caller may not author is
 * simply not in it: `null`, which every caller already treats as a 404.
 *
 * Every tier that reaches a collection is in one response (`loadTierRows`), so
 * one request answers for a collection row, a personal row and the global row
 * an override is being taken from alike.
 */
export async function getNotificationDef( id, { collectionId } = {} ) {
	const wanted = String( id );
	const params = collectionId ? { collection_id: String( collectionId ) } : { scope: 'global' };
	const { data } = await api.get( API_NOTIFICATION_DEFS_URL, { params } );
	const defs = Array.isArray( data?.defs ) ? data.defs : [];
	return defs.find( def => normalizeId( def?._id ) === wanted ) || null;
}

/**
 * Authoring writes. Every one can answer 400 with a field-level, human-written
 * message — the API validates a notification's whole shape at write time so an
 * author sees the problem here rather than at 3am — and callers surface
 * `error.response.data.message` VERBATIM, because paraphrasing it throws away
 * the only part that says which field is wrong.
 */
export async function createNotificationDef( payload ) {
	const { data } = await api.post( API_NOTIFICATION_DEFS_URL, payload );
	return data;
}

/** `key` is immutable, so the editor never sends one on a patch. */
export async function updateNotificationDef( id, payload ) {
	const { data } = await api.patch( API_NOTIFICATION_DEF_URL( id ), payload );
	return data;
}

/**
 * Drop this tier's row and let the cascade fall through to whatever is beneath
 * it — the `cardLayouts` "Reset to inherited" idiom verbatim. The row is
 * dropped, never blanked: a row of empty values and no row at all must not
 * mean two different things.
 */
export function deleteNotificationDef( id ) {
	return api.delete( API_NOTIFICATION_DEF_URL( id ) );
}

/**
 * The three definition endpoints that take a body all take THE SAME ONE, and
 * it is one key wide: `{collection_id?}` — which collection a GLOBAL
 * definition is being disabled in, previewed in or tested in. A collection's
 * own row and a personal row carry their own, so they need none.
 *
 * ⚠ THEY DO NOT TAKE A DRAFT. An earlier draft of this file posted the whole
 * unsaved payload here on the theory that a preview should not need a save;
 * the shipped endpoints read the STORED row by id and ignore everything else,
 * so what that actually bought was a misleading comment. Callers may hand this
 * a draft anyway — the editor's preview rail does — and it is narrowed to the
 * one field the server reads rather than put on the wire whole.
 */
const collectionBody = ( input = {} ) => {
	const id = input?.collection_id ?? input?.collectionId;
	return id ? { collection_id: String( id ) } : {};
};

/** A collection-tier tombstone over a global key: stop it here, don't fork it. */
export function disableNotificationDef( id, input ) {
	return api.post( API_NOTIFICATION_DEF_DISABLE_URL( id ), collectionBody( input ) );
}

/**
 * Render the SAVED notification against real data and send nothing: no
 * delivery row, no inbox row, no mail.
 *
 *   -> { title, body,
 *        trigger:  { summary, … },
 *        audience: { summary, count, userIds? },
 *        channels: { email|sms|push: { payload, problems? } },
 *        sample:   { item_id } | null,
 *        watch?:   { scanned, wouldFire, wouldPass, items[], truncated?, … },
 *        sent: false, warnings? }
 *
 * `watch` is the threshold's whole preview — which records are inside the
 * ladder today and at which rung — because a rendered body over the newest
 * record says nothing about whether anything is actually due.
 */
export async function previewNotificationDef( id, input ) {
	const { data } = await api.post( API_NOTIFICATION_DEF_PREVIEW_URL( id ), collectionBody( input ) );
	return data || null;
}

/**
 * Send one real message, to the caller and nobody else. The preview says what
 * it would look like; this says what it looks like in a mail client, which is
 * the only place a template's line breaks and button rows are really judged.
 *
 *   -> 202 { sent, to, byChannel, suppressed, unsupported, warnings? }
 */
export async function sendNotificationDefTest( id, input ) {
	const { data } = await api.post( API_NOTIFICATION_DEF_TEST_URL( id ), collectionBody( input ) );
	return data || null;
}
